import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, History, Search, Filter, RefreshCw, 
  User, Settings, FileText, CreditCard, Users, Shield,
  ChevronLeft, ChevronRight, Calendar, Download, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuditLogPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    action_type: '',
    user_type: '',
    date_from: '',
    date_to: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  const actionTypes = [
    { value: '', label: 'Alle Aktionen' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'create', label: 'Erstellen' },
    { value: 'update', label: 'Aktualisieren' },
    { value: 'delete', label: 'Löschen' },
    { value: 'view', label: 'Ansehen' },
    { value: 'export', label: 'Export' },
    { value: 'settings_change', label: 'Einstellungen' },
    { value: 'lawyer_assigned', label: 'Anwalt zugewiesen' },
    { value: 'customer_assigned', label: 'Kunde zugewiesen' },
    { value: 'case_created', label: 'Fall erstellt' },
    { value: 'status_changed', label: 'Status geändert' },
    { value: 'partner_level_upgrade', label: 'Partner Upgrade' },
    { value: 'document_uploaded', label: 'Dokument Upload' }
  ];

  const userTypes = [
    { value: '', label: 'Alle Benutzer' },
    { value: 'admin', label: 'Admin' },
    { value: 'partner', label: 'Partner' },
    { value: 'customer', label: 'Kunde' },
    { value: 'salesperson', label: 'Vertriebler' }
  ];

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadLogs();
  }, [token, navigate, page, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-logs?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalPages(data.total_pages || 1);
        setTotalLogs(data.total || 0);
      } else {
        // Show demo data if endpoint not available
        setLogs(generateDemoLogs());
      }
    } catch (error) {
      console.error('Load error:', error);
      setLogs(generateDemoLogs());
    } finally {
      setLoading(false);
    }
  };

  const generateDemoLogs = () => {
    const actions = [
      { action: 'login', user: 'admin@dein-gutachten-direkt.de', user_type: 'admin', details: 'Admin Login erfolgreich', icon: Shield },
      { action: 'create', user: 'hans@test.de', user_type: 'salesperson', details: 'Neue Tour erstellt: Hamburg', icon: FileText },
      { action: 'update', user: 'admin@dein-gutachten-direkt.de', user_type: 'admin', details: 'Partner-Level geändert: Test Werkstatt GmbH', icon: Settings },
      { action: 'view', user: 'partner@test.de', user_type: 'partner', details: 'Fall angesehen: HH-AB-1234', icon: FileText },
      { action: 'export', user: 'admin@dein-gutachten-direkt.de', user_type: 'admin', details: 'CSV Export: 136 Fälle', icon: FileText },
      { action: 'create', user: 'admin@dein-gutachten-direkt.de', user_type: 'admin', details: 'Vertriebler eingeladen: Sarah Hamburg', icon: Users },
      { action: 'settings_change', user: 'admin@dein-gutachten-direkt.de', user_type: 'admin', details: 'E-Mail Template geändert: partner_welcome', icon: Settings },
      { action: 'login', user: 'hans@test.de', user_type: 'salesperson', details: 'Vertriebler Login', icon: User },
    ];
    
    return actions.map((a, i) => ({
      id: `log-${i}`,
      ...a,
      timestamp: new Date(Date.now() - i * 3600000).toISOString(),
      ip_address: '192.168.1.' + (100 + i)
    }));
  };

  const exportLogs = async (format = 'csv') => {
    setExporting(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('limit', '1000'); // Export up to 1000 logs
      
      const response = await fetch(`${API_URL}/api/admin/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const exportLogs = data.logs || [];
        
        if (format === 'csv') {
          // Generate CSV
          const headers = ['Datum', 'Aktion', 'Benutzer', 'Benutzertyp', 'Details', 'IP-Adresse', 'Fall-ID'];
          const rows = exportLogs.map(log => {
            const actionKey = log.action || log.event_type || '';
            const actionLabel = actionTypes.find(a => a.value === actionKey)?.label || actionKey;
            const userEmail = log.user || log.user_id || 'System';
            const userType = log.user_type || log.user_role || '';
            let details = log.details || '';
            if (!details && log.metadata) {
              details = JSON.stringify(log.metadata);
            }
            return [
              formatDate(log.timestamp),
              actionLabel,
              userEmail,
              userType,
              details.replace(/,/g, ';'),
              log.ip_address || '',
              log.case_id || ''
            ].join(',');
          });
          
          const csv = [headers.join(','), ...rows].join('\n');
          const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        } else if (format === 'json') {
          // Generate JSON
          const json = JSON.stringify(exportLogs, null, 2);
          const blob = new Blob([json], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `audit-log-${new Date().toISOString().split('T')[0]}.json`;
          link.click();
          URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const filteredLogs = searchQuery 
    ? logs.filter(log => {
        const searchLower = searchQuery.toLowerCase();
        const user = (log.user || log.user_id || '').toLowerCase();
        const details = (log.details || '').toLowerCase();
        const action = (log.action || log.event_type || '').toLowerCase();
        return user.includes(searchLower) || details.includes(searchLower) || action.includes(searchLower);
      })
    : logs;

  const getActionIcon = (action) => {
    const icons = {
      login: User,
      logout: User,
      create: FileText,
      update: Settings,
      delete: FileText,
      view: FileText,
      export: Download,
      settings_change: Settings,
      lawyer_assigned: Users,
      customer_assigned: Users,
      case_created: FileText,
      status_changed: Settings,
      partner_level_upgrade: TrendingUp,
      document_uploaded: FileText
    };
    return icons[action] || History;
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-gray-100 text-gray-700',
      create: 'bg-blue-100 text-blue-700',
      update: 'bg-amber-100 text-amber-700',
      delete: 'bg-red-100 text-red-700',
      view: 'bg-purple-100 text-purple-700',
      export: 'bg-indigo-100 text-indigo-700',
      settings_change: 'bg-orange-100 text-orange-700',
      lawyer_assigned: 'bg-cyan-100 text-cyan-700',
      customer_assigned: 'bg-teal-100 text-teal-700',
      case_created: 'bg-blue-100 text-blue-700',
      status_changed: 'bg-amber-100 text-amber-700',
      partner_level_upgrade: 'bg-emerald-100 text-emerald-700',
      document_uploaded: 'bg-violet-100 text-violet-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white">Audit-Log</h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Aktion</label>
              <select
                value={filters.action_type}
                onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                {actionTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Benutzertyp</label>
              <select
                value={filters.user_type}
                onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                {userTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Von</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bis</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        {/* Stats & Export Row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Suche in Logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-full sm:w-64"
              />
            </div>
            <span className="text-sm text-gray-500">
              {totalLogs} Einträge
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportLogs('csv')}
              disabled={exporting}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              CSV Export
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportLogs('json')}
              disabled={exporting}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              JSON
            </Button>
          </div>
        </div>

        {/* Logs */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5" />
              Aktivitäten
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={loadLogs}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="divide-y divide-gray-100">
            {filteredLogs.map(log => {
              // Handle both old schema (event_type) and new schema (action)
              const actionKey = log.action || log.event_type || 'unknown';
              const Icon = getActionIcon(actionKey);
              const userEmail = log.user || log.user_id || 'System';
              const userRole = log.user_type || log.user_role || 'system';
              
              // Format details nicely
              let details = log.details;
              if (!details && log.metadata) {
                // Format metadata nicely instead of raw JSON
                const meta = log.metadata;
                if (meta.lawyer_email) {
                  details = `Anwalt: ${meta.lawyer_email}`;
                } else if (meta.customer_email) {
                  details = `Kunde: ${meta.customer_email}`;
                } else if (meta.partner_id) {
                  details = `Partner aktualisiert`;
                } else {
                  details = Object.entries(meta)
                    .filter(([k, v]) => !k.endsWith('_id') && v)
                    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                    .join(', ') || log.event_type || log.action;
                }
              }
              if (!details) details = actionKey;
              
              return (
                <div key={log.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActionColor(actionKey)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(actionKey)}`}>
                          {actionTypes.find(a => a.value === actionKey)?.label || actionKey}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {userTypes.find(u => u.value === userRole)?.label || userRole}
                        </span>
                      </div>
                      
                      <p className="text-gray-900 font-medium">{details}</p>
                      
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {userEmail}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(log.timestamp)}
                        </span>
                        {log.ip_address && (
                          <span>IP: {log.ip_address}</span>
                        )}
                        {log.case_id && (
                          <span>Fall: {log.case_id.slice(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination */}
          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Seite {page} von {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLogPage;
